import { useEffect, useMemo, useRef, useState } from 'react';
import styles from '../../MetaReportViewer.module.css';
import { Section } from '../../ReportUIPrimitives';
import { PerfField } from './common';
import { fmt } from './commonData';
import { analyzeShopMetaAd, getShopMetaAdAnalysis } from '../../../../utils/api/reportApi';
import { AdsBubbleMatrix, LeakWaterfallChart } from './funnelCharts';
import { useAnalysisFlow, AnalysisFlowLog } from './AnalysisFlowLog';

const formatMetric = (value, suffix = '') => (
  value == null ? 'Not available' : `${fmt(value)}${suffix}`
);

const buildEntries = (objectValue = {}) =>
  Object.entries(objectValue || {}).filter(([, value]) => value != null && value !== '');

const AdLevelAssessmentSection = ({
  matrix,
  selectedAdset,
  currency,
  userId,
  authToken,
  reportId,
  shopId,
  onAssessmentSaved,
  onShowCampaignPerformance,
}) => {
  const rows = useMemo(() => {
    const allRows = Array.isArray(matrix?.rows) ? matrix.rows : [];
    const adsetId = selectedAdset?.adset_id || '';
    const adsetName = selectedAdset?.adset_name || '';
    return allRows.filter((row) => (
      (adsetId && (row?.adset_id || '') === adsetId)
      || (adsetName && (row?.adset_name || '') === adsetName)
    ));
  }, [matrix, selectedAdset]);

  const [selectedKey, setSelectedKey] = useState(null);
  const [fetchedAssessment, setFetchedAssessment] = useState(null);
  const [isAssessmentLoading, setIsAssessmentLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const flow = useAnalysisFlow();
  const selectedKeyRef = useRef(null);

  useEffect(() => {
    if (!rows.length) {
      setSelectedKey(null);
      return;
    }
    const nextKey = rows[0]?.ad_id || rows[0]?.ad_name || null;
    setSelectedKey((prev) => {
      if (prev && rows.some((row) => (row.ad_id || row.ad_name) === prev)) return prev;
      return nextKey;
    });
  }, [rows]);

  const selectedRow = rows.find((row) => (row.ad_id || row.ad_name) === selectedKey) || rows[0] || null;
  const selectedAdKey = selectedRow?.ad_id || selectedRow?.ad_name || null;

  // Reset flow log when a different ad is selected
  useEffect(() => {
    if (selectedKeyRef.current === selectedAdKey) return;
    selectedKeyRef.current = selectedAdKey;
    flow.reset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAdKey]);

  const rowAssessment = selectedRow?.ads_level_assessment || null;
  const persistedAssessment =
    matrix?.assessments?.[selectedRow?.ad_id] ||
    matrix?.assessments?.[selectedRow?.ad_name] ||
    null;
  const selectedAssessment = rowAssessment || fetchedAssessment || persistedAssessment || null;

  useEffect(() => {
    let cancelled = false;
    setAnalysisError('');

    if (!selectedRow) {
      setFetchedAssessment(null);
      setIsAssessmentLoading(false);
      return undefined;
    }

    if (rowAssessment || persistedAssessment) {
      setFetchedAssessment(rowAssessment || persistedAssessment);
      setIsAssessmentLoading(false);
      return undefined;
    }

    if (!userId || !reportId) {
      setFetchedAssessment(null);
      setIsAssessmentLoading(false);
      return undefined;
    }

    setFetchedAssessment(null);
    setIsAssessmentLoading(true);

    getShopMetaAdAnalysis({
      userId,
      reportId,
      shopId,
      adId: selectedRow.ad_id,
      adName: selectedRow.ad_name,
      authToken,
    })
      .then((result) => {
        if (cancelled) return;
        setFetchedAssessment(result?.assessment || null);
      })
      .catch(() => {
        if (cancelled) return;
        setFetchedAssessment(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsAssessmentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    authToken,
    persistedAssessment,
    reportId,
    rowAssessment,
    selectedRow,
    selectedRow?.ad_id,
    selectedRow?.ad_name,
    shopId,
    userId,
  ]);

  if (!selectedAdset || !rows.length) return null;

  const detailFields = [
    { label: 'Format', value: selectedRow.creative_type || 'Other' },
    { label: 'Spend', value: currency ? `${currency} ${fmt(selectedRow.spend)}` : fmt(selectedRow.spend) },
    { label: 'CTR', value: formatMetric(selectedRow.ctr, '%') },
    { label: 'CPC', value: selectedRow.cpc != null ? (currency ? `${currency} ${fmt(selectedRow.cpc)}` : fmt(selectedRow.cpc)) : 'Not available' },
    { label: 'Landing Page Views', value: selectedRow.landing_page_views != null ? fmt(selectedRow.landing_page_views, 0) : 'Not available' },
    { label: 'LPV to Purchase Rate', value: formatMetric(selectedRow.landing_page_view_conversion_rate, '%') },
    { label: 'Attributed Revenue', value: selectedRow.attributed_revenue != null ? (currency ? `${currency} ${fmt(selectedRow.attributed_revenue)}` : fmt(selectedRow.attributed_revenue)) : 'Not available' },
    { label: 'ROAS', value: selectedRow.roas != null ? `${fmt(selectedRow.roas)}×` : 'Not available' },
    { label: 'Ad Share of Revenue', value: selectedRow.ad_share_of_revenue != null ? `${fmt((selectedRow.ad_share_of_revenue || 0) * 100)}%` : 'Not available' },
    { label: 'Attention Label', value: selectedRow.attention_label || '—' },
    { label: 'Traffic Label', value: selectedRow.traffic_quality_label || '—' },
    { label: 'Commercial Label', value: selectedRow.commercial_label || '—' },
    { label: 'Role Label', value: selectedRow.role_label || '—' },
    { label: 'Interpretation', value: selectedRow.creative_interpretation_label || '—' },
    { label: 'Future Action', value: selectedRow.creative_future_action || '—' },
  ];

  const clearReadingEntries = buildEntries(selectedAssessment?.clear_reading);
  const metricEntries = buildEntries(selectedAssessment?.core_metrics);

  const handleAnalyze = async () => {
    if (!userId || !selectedRow) return;
    setIsAnalyzing(true);
    setAnalysisError('');
    flow.start();
    try {
      const result = await analyzeShopMetaAd({
        userId,
        reportId,
        shopId,
        adId: selectedRow.ad_id,
        adName: selectedRow.ad_name,
        authToken,
      });
      flow.resolve();
      if (result?.ad_key && result?.assessment && typeof onAssessmentSaved === 'function') {
        setFetchedAssessment(result.assessment);
        onAssessmentSaved(result.ad_key, result.assessment);
      }
    } catch (error) {
      flow.fail(error?.message || 'Failed to analyze the selected ad.');
      setAnalysisError(error?.message || 'Failed to analyze the selected ad.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <Section title="Ad Level Assessment">
      <div className={styles.matrixShell}>
        <div className={styles.matrixSelectorCard}>
          <div className={styles.matrixSelectorHeader}>
            <p className={styles.matrixEyebrow}>Ads Under Selected Ad Set</p>
            <span className={styles.matrixSelectorCount}>{rows.length} total</span>
          </div>
          <div className={styles.matrixSelectorList}>
            {rows.map((row) => {
              const key = row.ad_id || row.ad_name || '';
              const isActive = key === (selectedRow?.ad_id || selectedRow?.ad_name);
              return (
                <button
                  key={key}
                  type="button"
                  className={`${styles.matrixSelectorItem} ${isActive ? styles.matrixSelectorItemActive : ''}`}
                  onClick={() => setSelectedKey(key)}
                >
                  <span className={styles.matrixSelectorName}>{row.ad_name || row.ad_id || 'Ad'}</span>
                  <span className={styles.matrixSelectorMeta}>
                    {row.creative_type || 'Other'} · {currency ? `${currency} ${fmt(row.spend)}` : fmt(row.spend)} spend
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <AdsBubbleMatrix
          rows={rows}
          selectedAdKey={selectedRow?.ad_id || selectedRow?.ad_name}
          onSelect={(key) => setSelectedKey(key)}
        />

        <LeakWaterfallChart row={selectedRow} />

        <div className={styles.matrixDetailCard}>
          <div className={styles.matrixDetailHeader}>
            <div>
              <p className={styles.matrixEyebrow}>Selected Ad</p>
              <h4 className={styles.matrixDetailTitle}>{selectedRow?.ad_name || selectedRow?.ad_id || 'Ad'}</h4>
            </div>
            <span className={styles.matrixConfidenceBadge}>
              {selectedRow?.creative_interpretation_label || selectedRow?.commercial_label || 'Ready'}
            </span>
          </div>
          <div className={styles.matrixActionRow}>
            {/* <button
              type="button"
              className={styles.matrixSecondaryButton}
              onClick={() => {
                if (!selectedRow || typeof onShowCampaignPerformance !== 'function') return;
                onShowCampaignPerformance({
                  campaignId: selectedRow.campaign_id || '',
                  campaignName: selectedRow.campaign_name || '',
                  adsetId: selectedRow.adset_id || '',
                  adsetName: selectedRow.adset_name || '',
                });
              }}
            >
              Show raw ads data
            </button> */}
            {(isAssessmentLoading || !selectedAssessment) ? (
              <button
                type="button"
                className={`${styles.matrixAnalyzeButton} ${(isAnalyzing || isAssessmentLoading) ? styles.matrixAnalyzeButtonBusy : ''}`}
                onClick={handleAnalyze}
                disabled={isAnalyzing || isAssessmentLoading || !userId}
              >
                {isAssessmentLoading ? 'Checking saved analysis…' : isAnalyzing ? 'Analyzing…' : 'Analyze Ad'}
              </button>
            ) : null}
            {analysisError ? <span className={styles.matrixActionError}>{analysisError}</span> : null}
          </div>
          <div className={styles.perfFields}>
            {detailFields.map((field) => (
              <PerfField key={field.label} label={field.label} value={field.value} />
            ))}
          </div>
        </div>

        <AnalysisFlowLog steps={flow.steps} label="Ad Analysis Progress" />

        {!isAssessmentLoading && selectedAssessment ? (
          <div className={styles.matrixAssessmentCard}>
            <div className={styles.matrixAssessmentHeader}>
              <p className={styles.matrixEyebrow}>Ad Analysis</p>
              <span className={styles.matrixAssessmentLabel}>
                {selectedAssessment?.ad_summary?.creative_interpretation_label || 'Analyzed'}
              </span>
            </div>
            <div className={styles.matrixAssessmentInner}>
              {selectedAssessment?.what_this_ad_did ? (
                <div className={styles.matrixAssessmentSection}>
                  <span className={styles.matrixAssessmentSectionTitle}>What This Ad Did</span>
                  <p className={styles.matrixAssessmentBody}>{selectedAssessment.what_this_ad_did}</p>
                </div>
              ) : null}
              {metricEntries.length ? (
                <div className={styles.matrixAssessmentSection}>
                  <span className={styles.matrixAssessmentSectionTitle}>Core Metrics</span>
                  <div className={styles.matrixAssessmentGrid}>
                    {metricEntries.map(([key, value]) => (
                      <div key={key} className={styles.matrixAssessmentMetric}>
                        <span className={styles.matrixAssessmentMetricLabel}>{key.replace(/_/g, ' ')}</span>
                        <span className={styles.matrixAssessmentMetricValue}>{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {clearReadingEntries.length ? (
                <div className={styles.matrixAssessmentSection}>
                  <span className={styles.matrixAssessmentSectionTitle}>Clear Reading</span>
                  <div className={styles.matrixAssessmentGrid}>
                    {clearReadingEntries.map(([key, value]) => (
                      <div key={key} className={styles.matrixAssessmentMetric}>
                        <span className={styles.matrixAssessmentMetricLabel}>{key.replace(/_/g, ' ')}</span>
                        <span className={styles.matrixAssessmentMetricValue}>{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {selectedAssessment?.next_step ? (
                <div className={styles.matrixAssessmentSection}>
                  <span className={styles.matrixAssessmentSectionTitle}>What To Do Next</span>
                  <p className={styles.matrixAssessmentBody}>
                    <strong>{selectedAssessment.next_step.future_action_label || 'Action'}:</strong>{' '}
                    {selectedAssessment.next_step.why || 'No rationale returned.'}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </Section>
  );
};

export default AdLevelAssessmentSection;
